import { AppState } from '@store/AppState';
import { theme } from '@theme';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDealType } from '../../../../../hooks/UseDealType';
import { usePortfolioBulkActionsContainer } from '../../../../../hooks/UsePortfolioBulkActionsContainer';
import { setIsInventoryBrowserModalOpen } from '../../../../../store/dealsheet/DealsheetActions';
import { DealsheetState } from '../../../../../store/dealsheet/DealsheetReducer';
import { SetInventoryRemainingModal } from '../../../../../store/inventory/InventoryActions';
import { setPortfolioShouldRefresh } from '../../../../../store/portfolio/PortfolioActions';
import { PortfolioResource } from '../../../../../types/PortfolioResource';
import InventoryBrowserModal from '../../../../dealsheet/shared/InventoryBrowserModal';
import HandleRemainingModal from '../../../../dealsheet/shared/modals/bulk-inventory-browser/HandleRemainingModal';
import ButtonComponent from '../../../../shared/button/ButtonComponent';
import BulkInventoryContainer from './BulkInventoryContainer';

type UpdateReplacementProps = {
  isAllRowsSelected: boolean;
  isClearSelectionLink: boolean;
  selectedRecords: PortfolioResource[];
  dealType: string;
  onCloseButtonClick: () => void;
};

const UpdateReplacement: FC<UpdateReplacementProps> = ({
  isAllRowsSelected,
  isClearSelectionLink,
  selectedRecords,
  onCloseButtonClick,
}) => {
  const { createUpdateReplacementPayload, dealAggregationsForUpdateReplacement } =
    usePortfolioBulkActionsContainer();
  const { isInventoryBrowserModalOpen } = useSelector<AppState, DealsheetState>(
    (state) => state.dealsheetModernized,
  );
  const remainingData = useSelector<AppState, any>(
    (state) => state.inventoryReducer.remainingBulkInventory,
  );
  const invRemainingModal = useSelector<AppState, any>(
    (state) => state.inventoryReducer.remainingModal,
  );

  type BulkDataSource = {
    type: string;
    RequestResource: object;
    isAllRowsSelected: boolean;
    dealerList: any[];
    selectedDealer: any[];
  };

  const [bulkDataSource, SetBulkDataSource] = useState<BulkDataSource>({
    type: '',
    RequestResource: {},
    isAllRowsSelected: false,
    dealerList: [],
    selectedDealer: [],
  });
  const [remainingModal, SetRemainingModal] = useState(false);
  const [remainingModalData, SetRemainingModalData] = useState<any>({});
  const dispatch = useDispatch();
  const pathParts = location.pathname.split('/');
  const urlType = pathParts[2];

  useEffect(() => {
    if (invRemainingModal) {
      SetRemainingModal(true);
      SetRemainingModalData(remainingData);
    }
  }, [invRemainingModal]);

  const openInventoryModal = async () => {
    const rowData = selectedRecords as PortfolioResource[];
    const dealerListTemp = [];
    const bulkData = {
      type: 'bulk-inventory',
      RequestResource: {},
      isAllRowsSelected: isClearSelectionLink,
      dealerList: [],
      selectedDealer: [],
    };

    if (!(isAllRowsSelected && isClearSelectionLink)) {
      const { getDealTypeLabel } = useDealType();
      const payload = rowData.map((row) => {
        if (!dealerListTemp.includes(row.dealerId)) dealerListTemp.push(row.dealerId);
        return {
          closedDealId: row.closedDealId,
          dealerId: row.dealerId,
          dealType: getDealTypeLabel(row.closedDeal.dealType),
        };
      });
      bulkData.dealerList = payload;
    } else {
      const payload = createUpdateReplacementPayload();
      bulkData.RequestResource = payload;
      await dealAggregationsForUpdateReplacement(payload).then((dealAggregations) => {
        bulkData.dealerList = dealAggregations;
        dealAggregations.forEach((deal) => {
          if (!dealerListTemp.includes(deal.dealerId)) dealerListTemp.push(deal.dealerId);
        });
      });
    }
    bulkData.selectedDealer = dealerListTemp;
    SetBulkDataSource(bulkData);
    dispatch(setIsInventoryBrowserModalOpen(true));
  };

  const handleModalCancel = () => {
    SetBulkDataSource({ ...bulkDataSource, selectedDealer: [] });
    onCloseButtonClick();
  };

  const dealerSelect = (selected) => {
    SetBulkDataSource({ ...bulkDataSource, selectedDealer: [selected.dealerId] });
  };

  const closeRemainingModal = () => {
    dispatch(SetInventoryRemainingModal(false));
    SetRemainingModal(false);
    dispatch(setPortfolioShouldRefresh(true, urlType));
  };

  const replaceRemaining = () => {
    SetBulkDataSource({ ...bulkDataSource, dealerList: remainingData.closedDealIds });
    SetRemainingModal(false);
    dispatch(SetInventoryRemainingModal(false));
    dispatch(setIsInventoryBrowserModalOpen(true));
  };

  return (
    <>
      <ButtonComponent
        color={theme.palette.mediumGray}
        wrapperBackgroundColor={theme.palette.blackOlive}
        text={'Update Replacement...'}
        fontWeight={theme.typography.fontWeights.semibold}
        outerPadding={'0px'}
        innerPadding={'7px 12px'}
        borderRadius={4}
        onClick={openInventoryModal}
        textAlign='left'
      />
      {bulkDataSource.selectedDealer && bulkDataSource.selectedDealer.length > 1 && (
        <BulkInventoryContainer
          isOpen={bulkDataSource.selectedDealer && bulkDataSource.selectedDealer.length > 1}
          title={'INVENTORY BROWSER'}
          handleCancelClick={handleModalCancel}
          showCloseIcon={true}
          dealerSelect={dealerSelect}
          availableDealerList={bulkDataSource.selectedDealer}
        />
      )}

      {bulkDataSource &&
        bulkDataSource.selectedDealer.length === 1 &&
        isInventoryBrowserModalOpen && (
          <InventoryBrowserModal
            bulkDataSource={bulkDataSource}
            onCloseButtonClick={onCloseButtonClick}
          />
        )}
      {remainingModal && (
        <HandleRemainingModal
          isOpen={remainingModal}
          closeModal={closeRemainingModal}
          data={remainingModalData}
          replaceRemaining={replaceRemaining}
        />
      )}
    </>
  );
};

export default UpdateReplacement;
